// ISS-295744: notes for a shipment tracked by a BL and/or a booking number.
// The working number's tracking is the main view; a problem with the other number is a note.

export type LookupStatus = 'PENDING' | 'SUCCESS' | 'FAILED' | 'UNAVAILABLE';

export interface NumberLookup {
  status: LookupStatus;
  message?: string;
}

export interface LookupCardData {
  blNumber?: string;
  bookingNumber?: string;
  hblNumber?: string;
  shippingLineName?: string;
  shippingLineId?: string;
  blLookup?: NumberLookup;
  bkLookup?: NumberLookup;
  containerNumbers?: string[] | null;
  noOfContainers?: number;
  originPortName?: string;
  loadingPortName?: string;
  destinationPortName?: string;
  dischargePortName?: string;
}

export interface LookupNote {
  key: string;
  text: string;
}

const isFailing = (lookup?: NumberLookup) => lookup?.status === 'FAILED' || lookup?.status === 'UNAVAILABLE';

// "undefined" / shippingLineId UNDEFINED is the placeholder for a shipment with no shipping line yet
const carrierName = (card: LookupCardData) => {
  const name = card.shippingLineName;
  if (!name || name.toLowerCase() === 'undefined' || card.shippingLineId === 'UNDEFINED') return 'the shipping line';
  return name;
};

const blLabel = (card: LookupCardData) => (card.hblNumber && !card.blNumber ? 'HBL' : 'BL');

const describeLookup = (label: string, numberValue: string | undefined, lookup: NumberLookup, card: LookupCardData) => {
  const subject = [label, numberValue].filter(Boolean).join(' ');
  return lookup.status === 'UNAVAILABLE' ? `${subject} not found with ${carrierName(card)}` : `${subject} couldn't be checked`;
};

export const getLookupNotes = (card: LookupCardData): LookupNote[] => {
  const { blLookup, bkLookup } = card;
  const anySuccess = blLookup?.status === 'SUCCESS' || bkLookup?.status === 'SUCCESS';
  if (!anySuccess) return [];

  const notes: LookupNote[] = [];
  if (bkLookup?.status === 'SUCCESS' && blLookup && isFailing(blLookup)) {
    notes.push({ key: 'bl', text: `${describeLookup(blLabel(card), card.blNumber || card.hblNumber, blLookup, card)} · tracking via booking` });
  }
  if (blLookup?.status === 'SUCCESS' && bkLookup && isFailing(bkLookup)) {
    notes.push({ key: 'bk', text: describeLookup('Booking', card.bookingNumber, bkLookup, card) });
  }
  const countKnown = Array.isArray(card.containerNumbers) || typeof card.noOfContainers === 'number';
  const containerCount = Math.max(card.containerNumbers?.length ?? 0, card.noOfContainers ?? 0);
  const hasPorts = Boolean(card.originPortName || card.loadingPortName || card.destinationPortName || card.dischargePortName);
  if (countKnown && containerCount === 0 && hasPorts) {
    notes.push({ key: 'containers', text: `Container details not yet shared by ${carrierName(card)}` });
  }
  return notes;
};

/**
 * Red lines to show when nothing works. [] means "nothing to show in red" (a number works, or nothing is failing);
 * null means the card has no lookup data (keep the old message).
 */
export const getLookupFailureLines = (card: LookupCardData): string[] | null => {
  const { blLookup, bkLookup } = card;
  if (!blLookup && !bkLookup) return null;
  if (blLookup?.status === 'SUCCESS' || bkLookup?.status === 'SUCCESS') return [];
  const lines: string[] = [];
  if (blLookup && isFailing(blLookup)) lines.push(blLookup.message || describeLookup(blLabel(card), card.blNumber || card.hblNumber, blLookup, card));
  if (bkLookup && isFailing(bkLookup)) lines.push(bkLookup.message || describeLookup('Booking', card.bookingNumber, bkLookup, card));
  return lines;
};
